module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<h3>Goal</h3>\n<p>Our goal for this chapter is to run following query:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code><span class=\"token keyword\">query</span> <span class=\"token punctuation\">{</span>\n  allLinks <span class=\"token punctuation\">{</span>\n    id\n    url\n    description\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p>The expected response is a list of links fetched from database.</p>\n<h3>Define a model</h3>\n<p>before we will add the first model, please decide where all models will be stored. I have one recommendation but of course you can place all\nmodels in the place you want.</p>\n<Instruction>\n<p>Create <code>models</code> package and <code>package.scala</code> with content:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n\n<span class=\"token keyword\">package</span> <span class=\"token keyword\">object</span> models <span class=\"token punctuation\">{</span>\n\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>From now on all the domain specific models will be placed in this file, so it will be easy to find them and in the case of this tutorial, easy to compare versions and recognize if something will need to change.</p>\n<p>Now we can go back to creating our first model.</p>\n<p>Let’s start by defining a really basic model <code>Link</code>.</p>\n<Instruction>\n<p>In the file created above add the following case class:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">case</span> <span class=\"token keyword\">class</span> Link<span class=\"token punctuation\">(</span>id<span class=\"token operator\">:</span> <span class=\"token builtin\">Int</span><span class=\"token punctuation\">,</span> url<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> description<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>As you can see, a <code>Link</code> model has fewer fields than in the schema you saw in the first chapter, but no worries, we will improve the model in the future.\nNow we’re focusing on completing execution stack so it would be better to keep this model simple.</p>\n<h4>Add <code>Link</code> support to database</h4>\n<p>Our simple database has to support this model and provide some data as well.</p>\n<Instruction>\n<p>Add following changes to the <code>DBSchema.scala</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">//in the imports section:</span>\n<span class=\"token keyword\">import</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria<span class=\"token punctuation\">.</span>models<span class=\"token punctuation\">.</span>_\n\n<span class=\"token comment\" spellcheck=\"true\">//In the object body:</span>\n\n<span class=\"token comment\" spellcheck=\"true\">//1</span>\n<span class=\"token keyword\">class</span> LinksTable<span class=\"token punctuation\">(</span>tag<span class=\"token operator\">:</span> Tag<span class=\"token punctuation\">)</span> <span class=\"token keyword\">extends</span> Table<span class=\"token punctuation\">[</span>Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>tag<span class=\"token punctuation\">,</span> <span class=\"token string\">\"LINKS\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">{</span>\n\n    <span class=\"token keyword\">def</span> id <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span><span class=\"token builtin\">Int</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"ID\"</span><span class=\"token punctuation\">,</span> O<span class=\"token punctuation\">.</span>PrimaryKey<span class=\"token punctuation\">,</span> O<span class=\"token punctuation\">.</span>AutoInc<span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">def</span> url <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span><span class=\"token builtin\">String</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"URL\"</span><span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">def</span> description <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span><span class=\"token builtin\">String</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"DESCRIPTION\"</span><span class=\"token punctuation\">)</span>\n\n    <span class=\"token keyword\">def</span> <span class=\"token operator\">*</span> <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>id<span class=\"token punctuation\">,</span> url<span class=\"token punctuation\">,</span> description<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>mapTo<span class=\"token punctuation\">[</span>Link<span class=\"token punctuation\">]</span>\n\n<span class=\"token punctuation\">}</span>\n\n<span class=\"token comment\" spellcheck=\"true\">//2</span>\n<span class=\"token keyword\">val</span> Links <span class=\"token operator\">=</span> TableQuery<span class=\"token punctuation\">[</span>LinksTable<span class=\"token punctuation\">]</span>\n\n<span class=\"token comment\" spellcheck=\"true\">//3</span>\n<span class=\"token keyword\">val</span> databaseSetup <span class=\"token operator\">=</span> DBIO<span class=\"token punctuation\">.</span>seq<span class=\"token punctuation\">(</span>\n    Links<span class=\"token punctuation\">.</span>schema<span class=\"token punctuation\">.</span>create<span class=\"token punctuation\">,</span>\n\n    Links forceInsertAll Seq<span class=\"token punctuation\">(</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">1</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"http://howtographql.com\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"Awesome community driven GraphQL tutorial\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">2</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"http://graphql.org\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"Official GraphQL web page\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">3</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"https://facebook.github.io/graphql/\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"GraphQL specification\"</span><span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">)</span>\n<span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>We just added database definition of our first model.</p>\n<ul>\n<li><strong>1</strong> defines mapping to database table,</li>\n<li><strong>2</strong> gives us a helper we will use to accessing data in this table.</li>\n<li>The last change, <strong>3</strong> is responsible for creating schema and adding three entities to the database. Don’t forget to replace the empty\nfunction which was provided by template</li>\n</ul>\n<h3>Providing GraphQL Context</h3>\n<p>Context is an object that flows across the whole execution, in the most cases this object doesn’t change at all.\nThe main responsibility of the Context is providing data and utils needed to fulfill the query.\nIn our example we will put there <code>DAO</code> so all queries will have access to the database.\nIn the future we will also put there authentication data.</p>\n<p>In our example Context will get a name <code>MyContext</code> and because it isn’t related with domain directly, I propose to keep it along\nother files in the <code>sangria</code> package.</p>\n<Instruction>\n<p>Create <code>MyContext</code> class:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n\n<span class=\"token keyword\">case</span> <span class=\"token keyword\">class</span> MyContext<span class=\"token punctuation\">(</span>dao<span class=\"token operator\">:</span> DAO<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>For now we don’t do anything with this file, but we had to create it to get the server working.\nFor sure we will back to this file in the future.</p>\n<h3>GraphQL Server</h3>\n<p>Time to implement the GraphQL Server. This object will be in the second layer of architecture, just after HTTP server.\nProper HTTP request will be converted into JSON object and sent to this server.\nGraphQL Server will parse that JSON as GraphQL query, execute it and through HTTP layer send response back to the client.\nIt will also catch GraphQL parsing errors and convert those into the proper HTTP responses.</p>\n<Instruction>\n<p>Create <code>GraphQLServer.scala</code> file:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>server<span class=\"token punctuation\">.</span>Route\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>parser<span class=\"token punctuation\">.</span>QueryParser\n<span class=\"token keyword\">import</span> spray<span class=\"token punctuation\">.</span>json<span class=\"token punctuation\">.</span><span class=\"token punctuation\">{</span>JsObject<span class=\"token punctuation\">,</span> JsString<span class=\"token punctuation\">,</span> JsValue<span class=\"token punctuation\">}</span>\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>model<span class=\"token punctuation\">.</span>StatusCodes<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>server<span class=\"token punctuation\">.</span>Directives<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> scala<span class=\"token punctuation\">.</span>concurrent<span class=\"token punctuation\">.</span>ExecutionContext\n<span class=\"token keyword\">import</span> scala<span class=\"token punctuation\">.</span>util<span class=\"token punctuation\">.</span><span class=\"token punctuation\">{</span>Failure<span class=\"token punctuation\">,</span> Success<span class=\"token punctuation\">}</span>\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>server<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>ast<span class=\"token punctuation\">.</span>Document\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>execution<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>marshallers<span class=\"token punctuation\">.</span>sprayjson<span class=\"token punctuation\">.</span>SprayJsonSupport<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>marshalling<span class=\"token punctuation\">.</span>sprayJson<span class=\"token punctuation\">.</span>_\n\n\n<span class=\"token keyword\">object</span> GraphQLServer <span class=\"token punctuation\">{</span>\n\n  <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n  <span class=\"token keyword\">private</span> <span class=\"token keyword\">val</span> dao <span class=\"token operator\">=</span> DBSchema<span class=\"token punctuation\">.</span>createDatabase\n\n  <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n  <span class=\"token keyword\">def</span> endpoint<span class=\"token punctuation\">(</span>requestJSON<span class=\"token operator\">:</span> JsValue<span class=\"token punctuation\">)</span><span class=\"token punctuation\">(</span><span class=\"token keyword\">implicit</span> ec<span class=\"token operator\">:</span> ExecutionContext<span class=\"token punctuation\">)</span><span class=\"token operator\">:</span> Route <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span>\n\n    <span class=\"token comment\" spellcheck=\"true\">// 3</span>\n    <span class=\"token keyword\">val</span> JsObject<span class=\"token punctuation\">(</span>fields<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span> requestJSON\n\n    <span class=\"token comment\" spellcheck=\"true\">// 4</span>\n    <span class=\"token keyword\">val</span> JsString<span class=\"token punctuation\">(</span>query<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span> fields<span class=\"token punctuation\">(</span><span class=\"token string\">\"query\"</span><span class=\"token punctuation\">)</span>\n\n    <span class=\"token comment\" spellcheck=\"true\">// 5</span>\n    QueryParser<span class=\"token punctuation\">.</span>parse<span class=\"token punctuation\">(</span>query<span class=\"token punctuation\">)</span> <span class=\"token keyword\">match</span> <span class=\"token punctuation\">{</span>\n      <span class=\"token keyword\">case</span> Success<span class=\"token punctuation\">(</span>queryAst<span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span>\n        <span class=\"token comment\" spellcheck=\"true\">// 6</span>\n        <span class=\"token keyword\">val</span> operation <span class=\"token operator\">=</span> fields<span class=\"token punctuation\">.</span>get<span class=\"token punctuation\">(</span><span class=\"token string\">\"operationName\"</span><span class=\"token punctuation\">)</span> collect <span class=\"token punctuation\">{</span>\n          <span class=\"token keyword\">case</span> JsString<span class=\"token punctuation\">(</span>op<span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> op\n        <span class=\"token punctuation\">}</span>\n\n        <span class=\"token comment\" spellcheck=\"true\">// 7</span>\n        <span class=\"token keyword\">val</span> variables <span class=\"token operator\">=</span> fields<span class=\"token punctuation\">.</span>get<span class=\"token punctuation\">(</span><span class=\"token string\">\"variables\"</span><span class=\"token punctuation\">)</span> <span class=\"token keyword\">match</span> <span class=\"token punctuation\">{</span>\n          <span class=\"token keyword\">case</span> Some<span class=\"token punctuation\">(</span>obj<span class=\"token operator\">:</span> JsObject<span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> obj\n          <span class=\"token keyword\">case</span> _ <span class=\"token keyword\">=></span> JsObject<span class=\"token punctuation\">.</span>empty\n        <span class=\"token punctuation\">}</span>\n        <span class=\"token comment\" spellcheck=\"true\">// 8</span>\n        complete<span class=\"token punctuation\">(</span>executeGraphQLQuery<span class=\"token punctuation\">(</span>queryAst<span class=\"token punctuation\">,</span> operation<span class=\"token punctuation\">,</span> variables<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n      <span class=\"token keyword\">case</span> Failure<span class=\"token punctuation\">(</span>error<span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span>\n        complete<span class=\"token punctuation\">(</span>BadRequest<span class=\"token punctuation\">,</span> JsObject<span class=\"token punctuation\">(</span><span class=\"token string\">\"error\"</span> <span class=\"token operator\">-</span><span class=\"token operator\">></span> JsString<span class=\"token punctuation\">(</span>error<span class=\"token punctuation\">.</span>getMessage<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">}</span>\n\n  <span class=\"token punctuation\">}</span>\n\n  <span class=\"token keyword\">private</span> <span class=\"token keyword\">def</span> executeGraphQLQuery<span class=\"token punctuation\">(</span>query<span class=\"token operator\">:</span> Document<span class=\"token punctuation\">,</span> operation<span class=\"token operator\">:</span> Option<span class=\"token punctuation\">[</span><span class=\"token builtin\">String</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">,</span> vars<span class=\"token operator\">:</span> JsObject<span class=\"token punctuation\">)</span><span class=\"token punctuation\">(</span><span class=\"token keyword\">implicit</span> ec<span class=\"token operator\">:</span> ExecutionContext<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span>\n    <span class=\"token comment\" spellcheck=\"true\">// 9</span>\n    Executor<span class=\"token punctuation\">.</span>execute<span class=\"token punctuation\">(</span>\n      GraphQLSchema<span class=\"token punctuation\">.</span>SchemaDefinition<span class=\"token punctuation\">,</span> <span class=\"token comment\" spellcheck=\"true\">// 10</span>\n      query<span class=\"token punctuation\">,</span> <span class=\"token comment\" spellcheck=\"true\">// 11</span>\n      MyContext<span class=\"token punctuation\">(</span>dao<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span> <span class=\"token comment\" spellcheck=\"true\">// 12</span>\n      variables <span class=\"token operator\">=</span> vars<span class=\"token punctuation\">,</span> <span class=\"token comment\" spellcheck=\"true\">// 13</span>\n      operationName <span class=\"token operator\">=</span> operation <span class=\"token comment\" spellcheck=\"true\">// 14</span>\n    <span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>map<span class=\"token punctuation\">(</span>OK <span class=\"token operator\">-</span><span class=\"token operator\">></span> _<span class=\"token punctuation\">)</span>\n      <span class=\"token punctuation\">.</span>recover <span class=\"token punctuation\">{</span>\n      <span class=\"token keyword\">case</span> error<span class=\"token operator\">:</span> QueryAnalysisError <span class=\"token keyword\">=></span> BadRequest <span class=\"token operator\">-</span><span class=\"token operator\">></span> error<span class=\"token punctuation\">.</span>resolveError\n      <span class=\"token keyword\">case</span> error<span class=\"token operator\">:</span> ErrorWithResolver <span class=\"token keyword\">=></span> InternalServerError <span class=\"token operator\">-</span><span class=\"token operator\">></span> error<span class=\"token punctuation\">.</span>resolveError\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>It’s one of the most important files in entire backend server so let’s analyze it step by step:</p>\n<ul>\n<li>\n<p><strong>1</strong> We need access to the database, so it’s the step where such connection is created.</p>\n</li>\n<li>\n<p><strong>2</strong> <code>endpoint</code> responds with <code>Route</code> type. It will be used directly in the routing of HTTP server. It expects JSON object as parameter.</p>\n</li>\n<li>\n<p><strong>3</strong> Main JSON Object is extracted from the root object and it consists three children. The expected structure you can see in the following fragment</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-none\"   nocopy=\"undefined\"><code>{\nquery: {},\nvariables: {},\noperationName: \"\"    \n}</code></pre>\n      </div>\n<p><code>query</code> is a query itself, <code>variables</code> is additional data for that query. In GraphQL you can send the query and arguments separately.\nYou can also set name for the query, it’s what the third object is for. Imagine that query is like a function,\nusually you’re using anonymous functions, but for logging or other purposes you could add names. It’s send as <code>operationName</code>.</p>\n</li>\n<li>\n<p><strong>4</strong> We’re extracting <code>query</code> from request at this point.</p>\n</li>\n<li>\n<p>When we have the query, we have to parse it. Sangria provides <code>QueryParser.parse</code> (<strong>5</strong>) function we can use in this case.\nWhen it fails, the server will respond with status 400 and error description in the body of response.\nAfter successful parsing, we’re also trying to extract the other two keys <code>operationName</code>(<strong>6</strong>) and <code>variables</code>(<strong>7</strong>).\nAt the end all those three objects are passed to the execution function (<strong>8</strong>).</p>\n</li>\n<li>\n<p><strong>9</strong> <code>Executor.execute</code> is the most important call in this class because it’s the point where the query is executed.\nIf the executor responds with success, the result is sent back to the client. In all other cases, the server will respond\nwith status code 4xx and some kind of explanation of what was wrong with the query. The Executor needs some data to fulfill\nthe request. Three of them are <code>query</code>(<strong>11</strong>), <code>operationName</code>(<strong>13</strong>) and <code>variables</code>(<strong>14</strong>) which are read from the request.\nThe last two are: <code>GraphQLSchema.SchemaDefinition</code> and <code>MyContext(dao)</code>.</p>\n</li>\n<li>\n<p><strong>12</strong> <code>MyContext</code> is a context object mentioned in the section above. In our example you can see that the context is\nbuilt with the DAO object within.</p>\n</li>\n<li>\n<p><code>GraphQLSchema.SchemaDefinition</code> is the last object we have to explain here. It contains our Schema - what we are able\nto query for. It also interprets how data is fetched and which data source it could use (i.e. one or more databases,\nREST call to the other server…). In short our <code>SchemaDefinition</code> file defines what we want to expose.\nThere are defined types (from GraphQL point of view) and shape of the schema a client is able to query for.\nBecause this file is still missing we will create it in the next step.</p>\n</li>\n</ul>\n<h3>Define GraphQLSchema</h3>\n<Instruction>\n<p>Create <code>GraphQLSchema</code> object:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>schema<span class=\"token punctuation\">.</span><span class=\"token punctuation\">{</span>Field<span class=\"token punctuation\">,</span> ListType<span class=\"token punctuation\">,</span> ObjectType<span class=\"token punctuation\">}</span>\n<span class=\"token keyword\">import</span> models<span class=\"token punctuation\">.</span>_\n<span class=\"token comment\" spellcheck=\"true\">// #</span>\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>schema<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>macros<span class=\"token punctuation\">.</span>derive<span class=\"token punctuation\">.</span>_\n\n<span class=\"token keyword\">object</span> GraphQLSchema <span class=\"token punctuation\">{</span>\n\n  <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n  <span class=\"token keyword\">val</span> LinkType <span class=\"token operator\">=</span> ObjectType<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n       <span class=\"token string\">\"Link\"</span><span class=\"token punctuation\">,</span>\n       fields<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n         Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"id\"</span><span class=\"token punctuation\">,</span> IntType<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>id<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n         Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"url\"</span><span class=\"token punctuation\">,</span> StringType<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>url<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n         Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"description\"</span><span class=\"token punctuation\">,</span> StringType<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>description<span class=\"token punctuation\">)</span>\n       <span class=\"token punctuation\">)</span>\n     <span class=\"token punctuation\">)</span>\n\n  <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n  <span class=\"token keyword\">val</span> QueryType <span class=\"token operator\">=</span> ObjectType<span class=\"token punctuation\">(</span>\n    <span class=\"token string\">\"Query\"</span><span class=\"token punctuation\">,</span>\n    fields<span class=\"token punctuation\">[</span>MyContext<span class=\"token punctuation\">,</span> <span class=\"token builtin\">Unit</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n      Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"allLinks\"</span><span class=\"token punctuation\">,</span> ListType<span class=\"token punctuation\">(</span>LinkType<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> c <span class=\"token keyword\">=></span> c<span class=\"token punctuation\">.</span>ctx<span class=\"token punctuation\">.</span>dao<span class=\"token punctuation\">.</span>allLinks<span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">)</span>\n\n  <span class=\"token comment\" spellcheck=\"true\">// 3</span>\n  <span class=\"token keyword\">val</span> SchemaDefinition <span class=\"token operator\">=</span> Schema<span class=\"token punctuation\">(</span>QueryType<span class=\"token punctuation\">)</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Sangria cannot reuse case classes defined in our domain, it needs its own object of type <code>ObjectType</code>. On the other hand, it allows us to decouple API/Sangria models from database representation. This abstraction allows us to freely hide, add or aggregate fields.</p>\n<ul>\n<li><strong>1</strong> is a definition of ObjectType for our <code>Link</code> class. First (String) argument defines the name in the schema. If you want it could differ from name of case class. In <code>fields</code> you have to define all those fields/functions you want to expose. Every field has to contain a <code>resolve</code> function which tells Sangria how to retrieve data for this field. As you can see there is also an explicitly defined type for that field.\nManual mapping could be boring in cases where you have to map many case classes. To avoid boilerplate code you can use the provided macro.</li>\n</ul>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">implicit</span> <span class=\"token keyword\">val</span> LinkType <span class=\"token operator\">=</span> deriveObjectType<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n<p>It will give the same result as the example I used in the code above.\nWhen you want to use macro-way to define objects don’t forget to import <code>sangria.macros.derive._</code></p>\n<ul>\n<li><strong>2</strong> <code>val QueryType</code> is a top level object of our schema. As you can see, the top level object has a name <code>Query</code>\nand it (along with nested objects) will be visible in the graphiql console that we will include later in this chapter.\nIn <code>fields</code> definition I’ve added only one <code>Field</code> at the moment</li>\n</ul>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code>  Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"allLinks\"</span><span class=\"token punctuation\">,</span> ListType<span class=\"token punctuation\">(</span>LinkType<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> c <span class=\"token keyword\">=></span> c<span class=\"token punctuation\">.</span>ctx<span class=\"token punctuation\">.</span>dao<span class=\"token punctuation\">.</span>allLinks<span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n<p>The snippet above defines a GraphQL field with name <code>allLinks</code>. It’s a list (ListType) of link items (LinkType).\nAS you can see it’s a definition of a query we want to provide in this chapter. Resolver needs a <code>allLinks</code> function in <code>DAO</code>\nobject so we have to implement it now.</p>\n<Instruction>\n<p>Add a <code>allLinks</code> function to the <code>DAO</code> object, the current state of this file should looks like the following:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n<span class=\"token keyword\">import</span> DBSchema<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> slick<span class=\"token punctuation\">.</span>jdbc<span class=\"token punctuation\">.</span>H2Profile<span class=\"token punctuation\">.</span>api<span class=\"token punctuation\">.</span>_\n\n<span class=\"token keyword\">class</span> DAO<span class=\"token punctuation\">(</span>db<span class=\"token operator\">:</span> Database<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n  <span class=\"token keyword\">def</span> allLinks <span class=\"token operator\">=</span> db<span class=\"token punctuation\">.</span>run<span class=\"token punctuation\">(</span>Links<span class=\"token punctuation\">.</span>result<span class=\"token punctuation\">)</span> \n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>GraphiQL console</h3>\n<p><a href=\"https://github.com/graphql/graphiql\">Graphiql</a> makes able to run queries against our server from the browser. Let’s implement it now.</p>\n<p>Giter8 template I provided for this example also contains proper file. You can find it in <code>src/main/resources/graphiql.html</code>.\nAll we need to do is to define the HTTP server is such way that this file will be exposed and available to be reached\nin the browser.</p>\n<h3>Configure HTTP Server endpoints</h3>\n<p>The last thing we have to do to fulfill this chapter’s goal is to configure HTTP server.\nWe have to expose <code>graphiql.html</code> file and open an endpoint where GraphQL queries will be send.</p>\n<Instruction>\n<p>Open the <code>Server.scala</code> file and replace <code>route</code> function with the following one:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">val</span> route<span class=\"token operator\">:</span> Route <span class=\"token operator\">=</span>\n    <span class=\"token punctuation\">(</span>post <span class=\"token operator\">&amp;</span> path<span class=\"token punctuation\">(</span><span class=\"token string\">\"graphql\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n      entity<span class=\"token punctuation\">(</span>as<span class=\"token punctuation\">[</span>JsValue<span class=\"token punctuation\">]</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span> requestJson <span class=\"token keyword\">=></span>\n        GraphQLServer<span class=\"token punctuation\">.</span>endpoint<span class=\"token punctuation\">(</span>requestJson<span class=\"token punctuation\">)</span>\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span> <span class=\"token operator\">~</span> <span class=\"token punctuation\">{</span>\n      getFromResource<span class=\"token punctuation\">(</span><span class=\"token string\">\"graphiql.html\"</span><span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>As you can see, a new <code>route</code> definition has only two endpoints.\nEvery <code>POST</code> to <code>/graphql</code> is delegated to GraphQLServer, response to every other request else is a content of the <code>graphiql.html</code> file.</p>\n<h3>Run the query</h3>\n<Instruction>\n<p>Run the server</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code>sbt run\n</code></pre>\n      </div>\n</Instruction>\n<p>And open in the browser an url <a href=\"http://localhost:8080/graphiql\">http://localhost:8080/graphiql</a>\nOf couse use different port number if you haven’t decided to default one during project initialization.\n<Instruction></p>\n<p>In graphiql console execute the following code:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code><span class=\"token keyword\">query</span> <span class=\"token punctuation\">{</span>\n  allLinks <span class=\"token punctuation\">{</span>\n    id\n    url\n    description\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>The response should looks like that:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-json\"   nocopy=\"false\"><code><span class=\"token punctuation\">{</span>\n  <span class=\"token property\">\"data\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n    <span class=\"token property\">\"allLinks\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">[</span>\n      <span class=\"token punctuation\">{</span>\n        <span class=\"token property\">\"id\"</span><span class=\"token operator\">:</span> <span class=\"token number\">1</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"url\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"http://howtographql.com\"</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"description\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"Awesome community driven GraphQL tutorial\"</span>\n      <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n      <span class=\"token punctuation\">{</span>\n        <span class=\"token property\">\"id\"</span><span class=\"token operator\">:</span> <span class=\"token number\">2</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"url\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"http://graphql.org\"</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"description\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"Official GraphQL web page\"</span>\n      <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n      <span class=\"token punctuation\">{</span>\n        <span class=\"token property\">\"id\"</span><span class=\"token operator\">:</span> <span class=\"token number\">3</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"url\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"https://facebook.github.io/graphql/\"</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"description\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"GraphQL specification\"</span>\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">]</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<h3>Goal achieved</h3>\n<p>In this chapter we’ve finished configuring the entire GraphQL server stack and defined a very basic first query.</p>",
			"fields": {
				"slug": "/graphql-scala/2-preparing-first-query/"
			},
			"excerpt": "Goal Our goal for this chapter is to run following query: The expected response is a list of links fetched from database. Define a model...",
			"frontmatter": {
				"title": "The first query",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/opt/build/repo/content/backend/graphql-scala/2-preparing-first-query.md absPath of file",
				"question": "Which key doesn't exists in the root JSON object.",
				"answers": [
					"operationName",
					"variables",
					"fields",
					"query"
				],
				"correctAnswer": 2,
				"description": "In this chapter you will learn last steps of preparation, add graphiql console for easy debugging and at the end, run your first query.",
				"pageTitle": "GraphQL Scala - First query"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding users",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/1-adding-users/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding new Snap settings to code",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/0-adding-new-Snap-settings-to-code/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "aws",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/4-aws/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "apache",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/2-apache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "custom css themes",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/10-custom-css-themes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "archive and delete",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/3-archive-and-delete/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "azure",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/5-azure/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "backup",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/6-backup/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "caddy webserver config",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/7-caddy-webserver-config/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloud foundry",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/8-cloud-foundry/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloudron",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan/9-cloudron/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-elixir/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Elixir & Absinthe as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-elixir/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Absinthe & Elixir. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-elixir/4-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with Absinthe, implement query resolvers in Elixir and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-yoga, Node.js & Prisma and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Node.js & Express as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-js/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Server and Database with the Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-js/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Building a Server with GraphQL and Graphene"
						},
						"fields": {
							"slug": "/graphql-python/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Setting up a Django Graphene Project"
						},
						"fields": {
							"slug": "/graphql-python/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Tutorial Summary"
						},
						"fields": {
							"slug": "/graphql-python/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Creating Data With Mutations"
						},
						"fields": {
							"slug": "/graphql-python/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Creating Your First Queries"
						},
						"fields": {
							"slug": "/graphql-python/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Authenticating on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Links and Voting",
							"description": "Enable Users to create Links and to Vote on them"
						},
						"fields": {
							"slug": "/graphql-python/5-links-and-voting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Handling Errors on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Filtering GraphQL Queries"
						},
						"fields": {
							"slug": "/graphql-python/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Simple Pagination with Graphene"
						},
						"fields": {
							"slug": "/graphql-python/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relay",
							"description": "Using Relay on Graphene"
						},
						"fields": {
							"slug": "/graphql-python/9-relay/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-java/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-java and best practices for defining the GraphQL schema in this tutorial."
						},
						"fields": {
							"slug": "/graphql-java/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with a Node.js & Express GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/10-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Alternative approaches to schema development",
							"description": "Learn about an alternative approach to developing schemas with graphl-java with graphql-spqr"
						},
						"fields": {
							"slug": "/graphql-java/11-alternative-approaches/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-java/12-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-java, implement query resolvers in Java and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-java & Java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connectors",
							"description": "Learn how to write connectors in Java to load data from MongoDB with graphql-java and a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/4-connectors/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Java."
						},
						"fields": {
							"slug": "/graphql-java/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Java and graphql-java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/6-more-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Subscriptions",
							"description": "Learn how to implement server-side GraphQL subscriptions with Java to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-java/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Java, Node.js & Express."
						},
						"fields": {
							"slug": "/graphql-java/7-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/9-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-ruby and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-ruby/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-ruby and best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-ruby/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-ruby, implement query resolvers in Ruby and use a GraphiQL Playground to test your queries."
						},
						"fields": {
							"slug": "/graphql-ruby/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Nodes",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/5-connecting-nodes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/graphql-scala/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "In this chapter will be described how to setup the HTTP server, install all dependencies and setup the database."
						},
						"fields": {
							"slug": "/graphql-scala/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-ruby & Ruby and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-ruby/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "The first query",
							"description": "In this chapter you will learn last steps of preparation, add graphiql console for easy debugging and at the end, run your first query."
						},
						"fields": {
							"slug": "/graphql-scala/2-preparing-first-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Handling Arguments",
							"description": "In this chapter you will learn how to use arguments and how to handle them and pass to the business logic."
						},
						"fields": {
							"slug": "/graphql-scala/3-handling_arguments/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Custom Scalars",
							"description": "In this chapter Link model will get an additional field to store date and time. You will learn how to handle such custom types and use scalars."
						},
						"fields": {
							"slug": "/graphql-scala/5-custom_scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deferred Resolvers",
							"description": "In this chapter you will learn how to improve performance with deferred resolvers."
						},
						"fields": {
							"slug": "/graphql-scala/4-deferred_resolvers/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Interfaces",
							"description": "In this chapter we will introduce two additional models. We show the most common parts and extract those as an interface. This time you will work alone."
						},
						"fields": {
							"slug": "/graphql-scala/6-interfaces/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relations",
							"description": "In this chapter you will learn how to connect models with relations. You will also learn how to prepare fetchers to use either relations or ids to get entities."
						},
						"fields": {
							"slug": "/graphql-scala/7-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "In this chapter you will learn how to add data to the database."
						},
						"fields": {
							"slug": "/graphql-scala/8-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Sangria's Middleware allows pre-processing queries. Gather all useful data in the following subqueries."
						},
						"fields": {
							"slug": "/graphql-scala/9-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding new Snap settings to code",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/0-adding-new-Snap-settings-to-code/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "adding users",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/1-adding-users/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "custom css themes",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/10-custom-css-themes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "apache",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/2-apache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "archive and delete",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/3-archive-and-delete/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "aws",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/4-aws/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "azure",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/5-azure/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "backup",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/6-backup/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "caddy webserver config",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/7-caddy-webserver-config/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloud foundry",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/8-cloud-foundry/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "cloudron",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/wekan-wiki/9-cloudron/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Angular & Apollo Client. You'll use angular-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/angular-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with angular-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with Angular and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/angular-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading link-items",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Angular & Apollo Client you learned how to build a production-ready Hackernews clone using angular-cli & Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's service injected in the component to define and send mutations."
						},
						"fields": {
							"slug": "/angular-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use Angular Router  together with GraphQL and Apollo Client to implement navigation in an Angular app."
						},
						"fields": {
							"slug": "/angular-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Angular app with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/angular-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & Angular. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/angular-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React & Apollo Client. Use create-react-app for the frontend and graphql-yoga & Prisma for the backend."
						},
						"fields": {
							"slug": "/react-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a Angular app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/angular-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Apollo Client you learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `<Mutation />` component to define and send mutations."
						},
						"fields": {
							"slug": "/react-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use react-router 4 together with GraphQL and Apollo Client to implement navigation in a React app. Each route will be represented as a `Link`."
						},
						"fields": {
							"slug": "/react-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Prisma provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/react-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a React app. The Prisma API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/react-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a production-ready Hackernews clone with GraphQL, React & Relay. You'll use create-react-app for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/react-relay/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL Fragments and Relay's QueryRenderer to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-relay/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use Relay Modern's new Mutation API to send mutations to a GraphQL server. With Relay Modern you can also update the cache directly after the mutation."
						},
						"fields": {
							"slug": "/react-relay/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": null
						},
						"fields": {
							"slug": "/react-relay/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Relay to provide an email-and-password-based login in a React app with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Relay Modern & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-relay/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Relay Modern's imperative API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-relay/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement cursor-based pagination with GraphQL and Relay Modern using `PaginationContainer`. Relay uses the concept of connections to represent lists."
						},
						"fields": {
							"slug": "/react-relay/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Relay Modern you learned how to build a production-ready Hackernews clone using create-react-app & Graphcool."
						},
						"fields": {
							"slug": "/react-relay/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Clients",
							"description": "Learn about GraphQL clients, like Relay and Apollo Client, and the features they provide, such as caching, schema validation and query/view colocation."
						},
						"fields": {
							"slug": "/advanced/0-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More GraphQL Concepts",
							"description": "Learn about advanced concepts of the GraphQL language like fragments, query arguments, aliases, interfaces and more SDL features"
						},
						"fields": {
							"slug": "/advanced/2-more-graphql-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Server",
							"description": "Learn about the major concepts of GraphQL servers, how it executes GraphQL queries with resolver functions and batches multiple requests."
						},
						"fields": {
							"slug": "/advanced/1-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Tooling and Ecosystem",
							"description": "Learn about the GraphQL ecosystem and its awesome tooling like the GraphiQL playgrounds or self-generating docs through introspection."
						},
						"fields": {
							"slug": "/advanced/3-tooling-and-ecosystem/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Security",
							"description": "Learn about different security aspects and strategies for GraphQL, such as timeouts, maximum query depth, query complexity and throttling."
						},
						"fields": {
							"slug": "/advanced/4-security/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common GraphQL questions about server-side caching, authentication, authorization, error handling, offline usage and more."
						},
						"fields": {
							"slug": "/advanced/5-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, VueJS & Apollo Client. You'll use vue-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/vue-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with VueJS & Apollo Client you learned how to build a production-ready Hackernews clone using vue-cli & Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `mutate` method to define and send mutations."
						},
						"fields": {
							"slug": "/vue-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use vue-router together with GraphQL and Apollo Client to implement navigation in a VueJS app."
						},
						"fields": {
							"slug": "/vue-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with vue-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with VueJS and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/vue-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a VueJS app with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/vue-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & VueJS. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/vue-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about GraphQL and why it is an API technology that's superior to REST. It is not only for React & Javascript developers but can be used for any API."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GraphQL is the better REST",
							"description": "Learn why GraphQL is a more efficient and flexible alternative to REST APIs. It has a strong type system and avoids frontend issues like over- and underfetching."
						},
						"fields": {
							"slug": "/basics/1-graphql-is-the-better-rest/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Core Concepts",
							"description": "Learn about the basic GraphQL language concepts, such as Queries, Mutations, Subscriptions and the GraphQL Schema & SDL. Try them out in your own sandbox."
						},
						"fields": {
							"slug": "/basics/2-core-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Ember, and Apollo Client. You'll use Ember and ember-apollo-client for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/ember-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Architecture)",
							"description": "Learn about different architectural use cases of GraphQL and the major components on the backend and the frontend, like resolver functions and client libraries."
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with ember-cli, ember-apollo-client, and the backend with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/ember-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigation",
							"description": "Learn how to use Ember components together with GraphQL and Apollo Client to implement navigation in an Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/4-navigation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use `ember-apollo-client` to send mutations."
						},
						"fields": {
							"slug": "/ember-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/ember-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Ember & Apollo Client you learned how to build a production-ready Hackernews clone using ember-apollo-client & Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/8-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a VueJS app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/vue-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Ember app with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/5-authentication/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/graphql-scala/2-preparing-first-query/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/graphql-scala-2-preparing-first-query.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/graphql-scala-2-preparing-first-query.json
// module chunks = 67